import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Github, ChevronDown, Brain, Server } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  {
    label: "Library",
    href: "/library",
    icon: Brain,
    description: "Node.js package for AI memory",
  },
  {
    label: "Server",
    href: "/server",
    icon: Server,
    description: "Self-hosted Docker infrastructure",
  },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-cortex-border bg-cortex-bg-darkest/80 backdrop-blur-xl">
      <nav className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <motion.div
            className="w-8 h-8 rounded-lg bg-gradient-to-br from-cortex-primary to-cortex-accent flex items-center justify-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Brain size={18} className="text-black" />
          </motion.div>
          <span className="font-bold text-xl text-cortex-text-primary">
            Cortex
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => {
            const isActive = location.pathname.startsWith(link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "text-cortex-primary"
                    : "text-cortex-text-secondary hover:text-cortex-text-primary"
                }`}
              >
                <span className="flex items-center gap-2">
                  <link.icon size={16} />
                  {link.label}
                </span>
                {isActive && (
                  <motion.div
                    layoutId="nav-indicator"
                    className="absolute inset-0 bg-cortex-primary/10 rounded-lg border border-cortex-primary/20"
                    transition={{ type: "spring", duration: 0.5 }}
                  />
                )}
              </Link>
            );
          })}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <a
            href="https://github.com/mzhub/cortex"
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-cortex-text-secondary hover:text-cortex-text-primary transition-colors"
          >
            <Github size={20} />
          </a>
          <Link to="/library/getting-started" className="btn-primary text-sm">
            Get Started
          </Link>
        </div>

        <button
          className="md:hidden p-2 text-cortex-text-secondary hover:text-cortex-text-primary transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-cortex-border bg-cortex-bg-dark overflow-hidden"
          >
            <div className="px-6 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 p-3 rounded-lg text-cortex-text-secondary hover:text-cortex-text-primary hover:bg-cortex-bg-elevated transition-all"
                >
                  <link.icon size={20} />
                  <div>
                    <div className="font-medium">{link.label}</div>
                    <div className="text-xs text-cortex-text-muted">
                      {link.description}
                    </div>
                  </div>
                </Link>
              ))}
              <div className="pt-4 border-t border-cortex-border">
                <Link
                  to="/library/getting-started"
                  onClick={() => setMobileOpen(false)}
                  className="btn-primary w-full text-sm"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
